import { cn } from "@/lib/utils";
import { useUseProcessTemplate } from "@/hooks/template";
import { useGetAllProcessFolders } from "@/hooks/process-folder/use-process-folder";
import { useAuthStore } from "@/store/use-auth-store";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import SearchGroup from "@/components/groups/search-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateName: string;
}

export const UseProcessTemplateModal: React.FC<Props> = ({
  isOpen,
  onClose,
  templateId,
  templateName,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const useMutation = useUseProcessTemplate();
  const { data: processFolders } = useGetAllProcessFolders();

  const [name, setName] = useState(templateName);
  const [groupId, setGroupId] = useState("");
  const [processFolderId, setProcessFolderId] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupId || !user) return;

    await useMutation.mutateAsync({
      id: templateId,
      data: {
        creatorId: user.id,
        groupId,
        name: name || undefined,
        processFolderId: processFolderId || undefined,
      },
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:rounded">
        <DialogHeader>
          <DialogTitle>{t("templates.useProcessModal.title")}</DialogTitle>
          <DialogDescription>
            {t("templates.useProcessModal.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="main-label">
              {t("templates.useProcessModal.nameLabel")}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-darkBlue focus:border-darkBlue sm:text-sm"
            />
          </div>

          <div className="mb-4">
            <label className="main-label">
              {t("templates.useProcessModal.groupLabel")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <SearchGroup setGroupId={setGroupId} groupId={groupId} />
          </div>

          {processFolders && processFolders.length > 0 && (
            <div className="mb-6">
              <label className="main-label">
                {t("templates.useProcessModal.folderLabel")}
              </label>
              <select
                value={processFolderId}
                onChange={(e) => setProcessFolderId(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-darkBlue focus:border-darkBlue sm:text-sm"
              >
                <option value="">— None —</option>
                {processFolders.map((folder: any) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!groupId || useMutation.isPending}
              className={cn(
                "main-dark-button",
                (!groupId || useMutation.isPending) && "opacity-50 cursor-not-allowed",
              )}
            >
              {useMutation.isPending
                ? "Creating..."
                : t("templates.useProcessModal.confirm")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
